import { useEffect, useMemo, useState } from "react";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { FormGrid, FieldInput, FieldTextarea, FieldSelect, FieldColor, FieldSwitch } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { FaBookOpen, FaInfoCircle, FaLink, FaPalette } from "react-icons/fa";
import "../../_Manuals/_UI/_Styles/Manuals.css";

const CATEGORIES = [
    { value: "GENERAL", label: "General" },
    { value: "PROCESS", label: "Proceso / paso a paso" },
    { value: "DEPLOY", label: "Deploy / publicación" },
    { value: "OPERATIONS", label: "Operaciones" },
    { value: "TROUBLESHOOTING", label: "Troubleshooting" },
    { value: "ONBOARDING", label: "Onboarding / puesta en marcha" },
    { value: "BACKUP", label: "Backup" },
    { value: "RECOVERY", label: "Recuperación / rollback" },
    { value: "RELEASE", label: "Release / versionado" },
    { value: "MONITORING", label: "Monitoreo" },
    { value: "SECURITY", label: "Seguridad" },
    { value: "TESTING", label: "Testing / QA" },
    { value: "INTEGRATION", label: "Integración" },
    { value: "AUTOMATION", label: "Automatización" },
    { value: "DOCUMENTATION", label: "Documentación" },
    { value: "LANGUAGE", label: "Lenguaje" },
    { value: "FRAMEWORK", label: "Framework" },
    { value: "BACKEND", label: "Backend" },
    { value: "FRONTEND", label: "Frontend" },
    { value: "FULLSTACK", label: "Full stack" },
    { value: "DATABASE", label: "Base de datos" },
    { value: "API", label: "API / servicios" },
    { value: "DEVOPS", label: "DevOps" },
    { value: "CLOUD", label: "Cloud" },
    { value: "TOOL", label: "Herramienta" },
    { value: "OTHER", label: "Otro" },
];

const EMPTY = {
    name: "", description: "", technology: "", version: "", category: "PROCESS",
    color: "#e83a52", officialDocsUrl: "", repositoryUrl: "", isFavorite: false,
};

const onValues = (group) => group ? {
    name: group.name ?? "",
    description: group.description ?? "",
    technology: group.technology ?? "",
    version: group.version ?? "",
    category: group.category ?? "PROCESS",
    color: group.color ?? "#e83a52",
    officialDocsUrl: group.officialDocsUrl ?? "",
    repositoryUrl: group.repositoryUrl ?? "",
    isFavorite: !!group.isFavorite,
} : EMPTY;

export const GuideGroupFormModal = ({ isOpen, onClose, group = null, onSave, isLoading = false, scopeLabel = "Proyecto" }) => {
    const [useValues, setValues] = useState(EMPTY);

    useEffect(() => {
        if (!isOpen) return;
        setValues(onValues(group));
    }, [isOpen, group]);

    const IS_EDIT = !!group?.id;
    const IS_DIRTY = useMemo(() => JSON.stringify(useValues) !== JSON.stringify(onValues(group)), [useValues, group]);

    const onField = (name) => ({
        id: name,
        name,
        value: useValues[name] ?? "",
        onChange: (event) => setValues((prev) => ({ ...prev, [name]: event.target.value })),
    });

    const onSubmit = async () => {
        if (!useValues.name.trim()) return;
        const ok = await onSave?.({
            ...useValues,
            name: useValues.name.trim(),
            technology: useValues.technology.trim() || undefined,
            version: useValues.version.trim() || undefined,
            officialDocsUrl: useValues.officialDocsUrl.trim() || undefined,
            repositoryUrl: useValues.repositoryUrl.trim() || undefined,
        });
        if (ok !== false) onClose?.();
    };

    return (
        <AppModal isOpen={isOpen} onClose={onClose} size="full" isDirty={IS_DIRTY} className="LStudio__manual_form_fullscreen"
            title={IS_EDIT ? "Configurar grupo de guias" : "Nuevo grupo de guias"}
            description={IS_EDIT
                ? "Define la identidad del grupo. Las guias y subguias se conservan aparte."
                : `Crea un contenedor de guias para ${scopeLabel.toLowerCase()}. Despues vas a crear cada guia particular dentro.`}
            footer={
                <>
                    <AppButton variant="ghost" tone="neutral" onClick={onClose}>Cancelar</AppButton>
                    <AppButton onClick={onSubmit} isLoading={isLoading}>{IS_EDIT ? "Guardar cambios" : "Crear grupo"}</AppButton>
                </>
            }>

            <div className="LStudio__manual_config_layout">
                <div className="LStudio__manual_config_form">
                    <div className="LStudio__app_priority_toggle">
                        <FieldSwitch id="isFavorite" name="isFavorite" label="Destacado" text="Mostrar este grupo primero"
                            hint="Fija este grupo arriba. La interfaz cambia de inmediato y se guarda sin volver a consultar la lista."
                            isChecked={!!useValues.isFavorite}
                            onChange={(event) => setValues((prev) => ({ ...prev, isFavorite: event.target.checked }))} />
                    </div>

                    <div className="LStudio__manual_config_intro">
                        <div className="LStudio__manual_config_intro_icon"><FaInfoCircle /></div>
                        <div>
                            <strong>Que es un grupo de guias</strong>
                            <p>Es el contenedor. Por ejemplo: “Deploy”, “Onboarding”, “Base de datos” o “Procedimientos”. Dentro vas a crear las guias concretas con pasos, comandos, checklists y notas.</p>
                        </div>
                    </div>

                    <div className="LStudio__manual_config_section">
                        <div className="LStudio__manual_config_section_title"><FaBookOpen /><span><strong>Identidad</strong><small>Como reconoces este grupo dentro del proyecto.</small></span></div>
                        <FormGrid columns={2}>
                            <FieldInput {...onField("name")} label="Nombre del grupo" placeholder="Deploy y produccion" isRequired
                                hint="Nombre amplio que agrupa varias guias relacionadas." />
                            <FieldInput {...onField("technology")} label="Tecnologia principal" placeholder="Docker / Rails / React"
                                hint="Opcional. Sirve para ubicar rapidamente el contexto tecnico." />
                            <FieldInput {...onField("version")} label="Version de referencia" placeholder="8.x / 20.x"
                                hint="Solo si los pasos dependen de una version concreta." />
                            <FieldSelect {...onField("category")} label="Categoria" options={CATEGORIES} isSearchable
                                hint="Ayuda a ordenar los grupos dentro del proyecto." />
                            <FieldTextarea {...onField("description")} label="Descripcion" placeholder="Procedimientos para desplegar, volver atras y validar produccion..." rows={4} span={2}
                                hint="Describe para que existe este grupo. El detalle real va dentro de cada guia." />
                        </FormGrid>
                    </div>

                    <div className="LStudio__manual_config_section">
                        <div className="LStudio__manual_config_section_title"><FaPalette /><span><strong>Apariencia</strong><small>Identificador visual del grupo.</small></span></div>
                        <FormGrid columns={2}>
                            <FieldColor {...onField("color")} label="Color identificador" showSwatches={false}
                                hint="Solo se usa como acento visual. No cambia el tema del editor." />
                            <div className="LStudio__manual_config_color_preview" style={{ "--manual-preview-color": useValues.color || "#e83a52" }}>
                                <span><FaBookOpen /></span>
                                <div><strong>{useValues.name || "Tu grupo de guias"}</strong><small>{scopeLabel}</small></div>
                            </div>
                        </FormGrid>
                    </div>

                    <div className="LStudio__manual_config_section">
                        <div className="LStudio__manual_config_section_title"><FaLink /><span><strong>Referencias</strong><small>Atajos opcionales relacionados con estas guias.</small></span></div>
                        <FormGrid columns={2}>
                            <FieldInput {...onField("officialDocsUrl")} label="Documentacion oficial" placeholder="https://..."
                                hint="Documentacion externa que quieras tener a mano." />
                            <FieldInput {...onField("repositoryUrl")} label="Repositorio / referencia" placeholder="https://github.com/..."
                                hint="Repositorio, wiki o referencia tecnica asociada." />
                        </FormGrid>
                    </div>

                </div>

                <aside className="LStudio__manual_config_guide">
                    <span className="LStudio__manual_config_guide_badge"><FaInfoCircle /> Modo guia</span>
                    <h3>Ejemplo practico</h3>
                    <p>Si estas dentro del subproyecto <strong>FRONTEND</strong>, podes crear un grupo llamado:</p>
                    <div className="LStudio__manual_config_guide_example">
                        <span>Grupo</span>
                        <strong>Deploy y produccion</strong>
                        <small>Proceso · React · Node</small>
                        <p>Adentro: “Deploy a Hostinger”, “Rollback”, “Variables de entorno”, “Checklist pre-release”, “Solucionar build roto”.</p>
                    </div>
                    <ol>
                        <li>Crea un grupo para un tema amplio.</li>
                        <li>Entra al grupo y crea una guia concreta.</li>
                        <li>Agrega subguias cuando necesites dividir el proceso.</li>
                        <li>Usa Markdown, codigo y checklist igual que en Manuales.</li>
                    </ol>
                    <p className="LStudio__manual_config_guide_note"><strong>Importante:</strong> archivar el grupo no borra nada. Se mueve a Archivados y desde ahi decidis si restaurarlo o eliminarlo definitivamente.</p>
                </aside>
            </div>
        </AppModal>
    );
};
